import { useTranslation } from "react-i18next";
import { Collapsible, CollapsibleContent, CollapsibleTrigger, cn, StringSelect } from "@tellescope/ui";
import { ChevronDown } from "lucide-react";
import type { BlockStyle } from "../../../../../shared/schema";
import { ColorField } from "./ColorField";

interface StyleSectionProps {
  style: BlockStyle | undefined;
  onChange: (style: BlockStyle | undefined) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const StyleSection = ({ style, onChange, open, onOpenChange }: StyleSectionProps) => {
  const { t } = useTranslation();

  const update = (updates: Partial<BlockStyle>) => {
    const next = { ...style, ...updates };
    const hasValues = Object.values(next).some((v) => v !== undefined);
    onChange(hasValues ? next : undefined);
  };

  const hasCustomStyle = style && Object.values(style).some((v) => v !== undefined);

  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className="border-t pt-4 mt-4">
      <CollapsibleTrigger className="flex items-center gap-2 w-full text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
        <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
        {t("Style Settings")}
        {hasCustomStyle && (
          <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">{t("Custom")}</span>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-4 space-y-4">
        <StringSelect
          label={t("Padding")}
          value={style?.padding ?? "none"}
          onValueChange={(value) =>
            update({ padding: value === "none" ? undefined : (value as BlockStyle["padding"]) })
          }
          options={[
            { value: "none", label: t("None") },
            { value: "sm", label: t("Small") },
            { value: "md", label: t("Medium") },
            { value: "lg", label: t("Large") },
          ]}
        />
        <StringSelect
          label={t("Text Alignment")}
          value={style?.textAlign ?? "left"}
          onValueChange={(value) =>
            update({ textAlign: value === "left" ? undefined : (value as BlockStyle["textAlign"]) })
          }
          options={[
            { value: "left", label: t("Left") },
            { value: "center", label: t("Center") },
            { value: "right", label: t("Right") },
          ]}
        />
        <ColorField
          label={t("Text Color")}
          value={style?.textColor}
          onChange={(color) => update({ textColor: color })}
        />
        <ColorField
          label={t("Background Color")}
          value={style?.backgroundColor}
          onChange={(color) => update({ backgroundColor: color })}
        />
      </CollapsibleContent>
    </Collapsible>
  );
};
